import type { ProjectSummary, SummaryChange } from '~/calculations/projectSummary'
import { PHASE_LABELS } from '~/models/phase'
import type { ProjectRecord } from '~/models/project'
import { DB_SCHEMA_VERSION, EXPORT_FORMAT_VERSION } from '~/models/schemaVersion'
import type { IsoTimestamp } from '~/types/dates'
import type { PrivacySelection } from '../scope'
import { gated, missing, present, presentOrMissing } from '../values'

export const COMPLETION_EXPORT_FORMAT = 'project-sola-project-summary'

/**
 * The project completion summary, as its own export (D-062).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  A SEPARATE EXPORT, AND THAT IS THE PRIVACY DECISION.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * A finished project spans months. Folding it into the weekly document would
 * make a project-lifetime disclosure the default for every AI review, and
 * D-019's current-week-plus-four default exists precisely to stop that. So this
 * is user-initiated, on its own, for one project the user chose.
 *
 * **It reports counts and coverage. It never reports a score.** No consistency
 * figure, no grade, no percentage, no streak — and no grading against the
 * success criteria the route engine stated at setup, because a met/not-met
 * result is a grade in all but name (D-062, D-071).
 *
 * The same export rules as the weekly document apply in full: nothing is a bare
 * `null`, every absent value carries a reason, and rounding happens here rather
 * than in storage or calculation (D-039, D-041).
 */
export interface CompletionSummaryModel {
  readonly generatedAt: IsoTimestamp
  readonly project: ProjectRecord
  readonly summary: ProjectSummary
  readonly selection: PrivacySelection
  /** Phase types the project passed through, oldest first. */
  readonly phaseTypes: readonly string[]
}

export function serializeCompletionSummary(model: CompletionSummaryModel): Record<string, unknown> {
  const { project, summary, selection } = model

  return {
    format: COMPLETION_EXPORT_FORMAT,
    formatVersion: EXPORT_FORMAT_VERSION,
    databaseSchemaVersion: DB_SCHEMA_VERSION,
    generatedAt: model.generatedAt,

    privacySelection: {
      includeNotes: selection.includeNotes,
      includeExactDates: selection.includeExactDates,
    },

    project: {
      /* Free text the user wrote, so it gates on notes — people name projects
         after weddings, holidays and other people. */
      name: gated(
        selection.includeNotes,
        'privacySelection.includeNotes',
        project.name,
        'notNamed',
      ),
      desiredOutcome: presentOrMissing(project.desiredOutcome, 'predatesProjectSetup'),
      status: present(project.status),
      startDate: gated(
        selection.includeExactDates,
        'privacySelection.includeExactDates',
        project.startDate as string,
        'notRecorded',
      ),
      endedOn: gated(
        selection.includeExactDates,
        'privacySelection.includeExactDates',
        project.endedOn as string | undefined,
        'stillRunning',
      ),
      /* Why it ended is the user's own sentence, so it gates on notes. */
      endReason: gated(
        selection.includeNotes,
        'privacySelection.includeNotes',
        project.endReason,
        'noReasonRecorded',
      ),
      phases: model.phaseTypes.map((type) => present(type)),
    },

    /*
     * What was recorded.
     *
     * Counts, in the app's own units, with no ratio and no proportion anywhere.
     * A reader can divide these themselves; what matters is that Project Sola
     * does not present the result as a verdict on the person.
     */
    whatWasRecorded: {
      durationDays: present(summary.durationDays, 'days'),
      durationWeeks: present(summary.durationWeeks, 'weeks'),
      phaseCount: present(summary.phaseCount, 'phases'),
      weighIns: present(summary.weighInsRecorded, 'entries'),
      measurements: present(summary.measurementsRecorded, 'sessions'),
      reviewsCompleted: present(summary.reviewsCompleted, 'reviews'),
    },

    /* The existing D-036 vocabulary — sufficient, limited, insufficient —
       rather than a second way of saying the same thing. */
    coverage: {
      weighIns: present(summary.coverage.weighIns),
      measurements: present(summary.coverage.measurements),
      reviews: present(summary.coverage.reviews),
    },

    whatChanged: {
      weightKg: serializeChange(summary.weightChangeKg, 'kg'),
      waistCm: serializeChange(summary.waistChangeCm, 'cm'),
      bodyFatEstimatePercent: serializeChange(summary.estimateChangePercent, '%'),
    },

    /*
     * Deliberately absent, and worth saying so in the document itself.
     *
     * A reader — human or otherwise — asked to review a body project will look
     * for a score, and finding none may assume it was omitted by accident.
     */
    notReported: {
      consistencyScore: missing('scoresAreNotPartOfThisProduct'),
      grade: missing('scoresAreNotPartOfThisProduct'),
      streak: missing('scoresAreNotPartOfThisProduct'),
    },

    /* Estimated strength retention is intended and is not here: it needs
       sentinel-lift history across the whole project, which the summary does
       not yet gather (D-062). Named rather than silently absent. */
    strengthRetention: missing('notYetSummarisedAcrossAProject'),
  }
}

/**
 * A change, or the reason there is not one.
 *
 * **A missing value is never a zero.** A project with one measurement has no
 * waist change, and reporting `0` would say the waist did not move — a
 * different claim entirely, and a false one.
 */
function serializeChange(change: SummaryChange, unit: string): unknown {
  if (change.kind === 'noComparison') {
    return missing(
      change.reason === 'onlyOne'
        ? 'onlyOneObservationRecorded'
        : change.reason === 'noStart'
          ? 'noObservationsRecorded'
          : 'noClosingObservation',
    )
  }

  return present({ from: change.from, to: change.to, delta: change.delta }, unit)
}

/** Display labels for the phase types a project passed through. */
export function phaseLabelsFor(types: readonly string[]): readonly string[] {
  return types.map((type) => PHASE_LABELS[type as keyof typeof PHASE_LABELS] ?? type)
}
